/**
 * Phase 14.5 — QorBOM Docs · 3-column layout shell.
 *
 * Stripe / Mintlify-style page chrome:
 *
 *   ┌──────────── QorbomNav (top, sticky) ────────────┐
 *   │                                                  │
 *   │  ┌──────────┐  ┌────────────────┐  ┌─────────┐ │
 *   │  │ Left     │  │   Main content │  │ Right   │ │
 *   │  │ rail     │  │   (slot)       │  │ rail    │ │
 *   │  │ (nav)    │  │                │  │ (TOC)   │ │
 *   │  └──────────┘  └────────────────┘  └─────────┘ │
 *   │                                                  │
 *   │            QorbomNav footer slice                │
 *   └──────────────────────────────────────────────────┘
 *
 * Each page composes itself by importing this layout, mounting its
 * static JSX into the main slot via `children`, and letting the
 * RightRailToc auto-extract anchors from h2/h3 with IDs.
 */
import React, { useRef } from "react";
import { Link } from "react-router-dom";

import QorbomNav            from "../QorbomNav";
import QorbomBrand          from "../qorbom/QorbomBrand";
import LeftRail, { QORBOM_DOCS_SECTIONS } from "./LeftRail";
import RightRailToc         from "./RightRailToc";
import QorbomDocsTour       from "./QorbomDocsTour";


export default function QorbomDocsLayout({
  children,
  testid = "qorbom-docs-layout",
}) {
  const contentRef = useRef(null);

  return (
    <div
      data-qorbom-light
      className="min-h-screen bg-white text-slate-900"
      style={{ fontFamily: '"IBM Plex Sans", system-ui, sans-serif' }}
      data-testid={testid}
    >
      {/* Top brand nav (sticky) */}
      <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-sm">
        <QorbomNav active="docs" />
      </div>

      <div className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6">
        <div className="grid gap-10 md:grid-cols-[220px_1fr] lg:grid-cols-[220px_minmax(0,1fr)_220px]">
          {/* Left rail (sticky on md+) */}
          <div className="md:sticky md:top-24 md:self-start md:max-h-[calc(100vh-7rem)] md:overflow-y-auto">
            <LeftRail sections={QORBOM_DOCS_SECTIONS} />
          </div>

          {/* Main content slot */}
          <main
            ref={contentRef}
            className="min-w-0 max-w-3xl"
            data-testid="qorbom-docs-main"
          >
            {children}

            {/* Edit-this-page / feedback footer — every page gets it */}
            <DocsPageFooter />
          </main>

          {/* Right rail TOC (lg+) */}
          <RightRailToc contentRef={contentRef} />
        </div>
      </div>

      {/* Brand footer */}
      <DocsFooter />

      {/* First-time onboarding tour — once per session via sessionStorage. */}
      <QorbomDocsTour />
    </div>
  );
}


function DocsPageFooter() {
  return (
    <footer className="mt-16 border-t border-slate-200 pt-8 text-sm text-slate-500"
            data-testid="qorbom-docs-page-footer">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          Need help?{" "}
          <a
            href="mailto:partners@qortrace.com"
            className="font-medium text-blue-700 hover:underline"
            data-testid="qorbom-docs-contact-link"
          >
            partners@qortrace.com
          </a>
        </div>
        <Link
          to="/qorbom/become-a-partner"
          className="font-medium text-blue-700 hover:underline"
          data-testid="qorbom-docs-apply-cta"
        >
          Get an API key →
        </Link>
      </div>
    </footer>
  );
}


function DocsFooter() {
  return (
    <footer className="mt-20 border-t border-slate-200 bg-white">
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-4 px-6 py-10 text-sm text-slate-500">
        <div className="flex items-center gap-2">
          <QorbomBrand
            className="font-bold text-slate-900"
            tmClassName="ml-0.5 align-super text-[0.55em] font-semibold text-blue-700"
          />
          <span>· Partner API · A QorTrace product</span>
        </div>
        <div className="flex gap-6">
          <Link to="/qorbom/become-a-partner" className="hover:text-slate-900">Become a partner</Link>
          <Link to="/qorbom/trust"             className="hover:text-slate-900">Trust</Link>
          <Link to="/legal/privacy"            className="hover:text-slate-900">Privacy</Link>
          <Link to="/legal/terms"              className="hover:text-slate-900">Terms</Link>
        </div>
      </div>
      <div className="mx-auto max-w-[1400px] px-6 pb-6 text-[10px] text-slate-400">
        QorBOM™ is a trademark of QorTrace. All rights reserved.
      </div>
    </footer>
  );
}
